import React, { useState, useEffect, useRef } from 'react';
import { safeString, safeUrl, formatCurrency } from './utils/helpers';
import { API_BASE_URL } from './utils/constants';
import {
  SearchIcon,
  HomeIcon,
  CalendarIcon,
  InfoIcon,
  XIcon,
  LoaderIcon,
  AlertCircleIcon,
  MapIcon,
  HeartIcon,
  ShareIcon,
  TagIcon
} from './components/icons';

// Feature Components
import PropertyMap from './components/features/PropertyMap';
import PropertyDetail from './components/features/PropertyDetail';
import PropertyCard from './components/features/PropertyCard';
import FavoritesView from './components/features/FavoritesView';
import CollectionsView from './components/features/CollectionsView';
import PropertyTagManager from './components/features/PropertyTagManager';
import SignInModal from './components/features/SignInModal';
import UserProfileDropdown from './components/features/UserProfileDropdown';
import DataMigrationPrompt from './components/features/DataMigrationPrompt';

// Custom Hooks
import { useFavorites, useTags, useNotes } from './hooks/useCloudflareStorage';
import { useAuth } from './hooks/useAuth';
import usePropertyFilters from './hooks/usePropertyFilters';
import { useSalesSearch } from './hooks/useSalesSearch';

// UI Components
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Alert, AlertDescription } from './components/ui/alert';
import { Field, FieldLabel } from './components/ui/field';
import { Input } from './components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from './components/ui/input-group';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './components/ui/select';
import { Badge } from './components/ui/badge';

console.log('🔄 PropertyExplorer v2.0 - Updated with bulletproof error handling');

const PropertyExplorer = () => {
  // Authentication hook
  const auth = useAuth();
  

  // Load saved data from localStorage on component mount
  const loadSavedData = () => {
    try {
      const savedFilters = localStorage.getItem('winnipegPropertyFilters');
      const savedProperties = localStorage.getItem('winnipegPropertyResults');
      const savedTotalCount = localStorage.getItem('winnipegPropertyTotalCount');

      return {
        filters: savedFilters ? JSON.parse(savedFilters) : {
          streetName: '',
          minYear: '',
          maxYear: '',
          minArea: '',
          maxArea: '',
          minValue: '',
          maxValue: '',
          propertyType: '',
          buildingType: '',
          basement: '',
          garage: '',
          pool: '',
          airConditioning: '',
          riverAdjacent: false,
          favoritesOnly: false,
          selectedTags: [],
          minSalePrice: '',
          maxSalePrice: '',
          minSaleYear: '',
          maxSaleYear: '',
          salesBuildingType: '',
          salesAddress: ''
        },
        properties: savedProperties ? JSON.parse(savedProperties) : [],
        totalCount: savedTotalCount ? JSON.parse(savedTotalCount) : null
      };
    } catch (err) {
      console.error('Error loading saved data:', err);
      return {
        filters: {
          streetName: '',
          minYear: '',
          maxYear: '',
          minArea: '',
          maxArea: '',
          minValue: '',
          maxValue: '',
          propertyType: '',
          buildingType: '',
          basement: '',
          garage: '',
          pool: '',
          airConditioning: '',
          riverAdjacent: false,
          favoritesOnly: false,
          selectedTags: [],
          minSalePrice: '',
          maxSalePrice: '',
          minSaleYear: '',
          maxSaleYear: '',
          salesBuildingType: '',
          salesAddress: ''
        },
        properties: [],
        totalCount: null
      };
    }
  };

  const savedData = loadSavedData();
  
  // Cloudflare D1 hooks for favorites and tags
  const { 
    favorites, 
    loading: favoritesLoading, 
    toggleFavorite, 
    clearAllFavorites, 
    isFavorite 
  } = useFavorites();
  
  const {
    notes,
    loading: notesLoading,
    saveNote,
    deleteNote,
    getNote,
    hasNote
  } = useNotes();

  const {
    tags,
    loading: tagsLoading,
    createNewTag,
    updateTag,
    deleteTag,
    addPropertyToTag,
    removePropertyFromTag,
    getPropertyTags: getPropertyTagsAsync,
    getAllTags: getAllTagsAsync
  } = useTags();

  // Sales search hook
  const {
    salesResults,
    loading: salesLoading,
    error: salesError,
    totalCount: salesTotalCount,
    searchSales,
    fetchPropertiesFromRollNumbers,
    clearResults: clearSalesResults
  } = useSalesSearch();

  // Sales search state
  const [salesProperties, setSalesProperties] = useState([]);
  const [salesSearchPerformed, setSalesSearchPerformed] = useState(false);
  const [salesViewMode, setSalesViewMode] = useState('grid');
  const [salesLoadingMore, setSalesLoadingMore] = useState(false);

  // Authentication state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showMigrationPrompt, setShowMigrationPrompt] = useState(false);
  
  // Create synchronous wrappers for components that expect sync access
  const getAllTags = () => {
    return Object.values(tags);
  };
  
  const getPropertyTags = (propertyId) => {
    const allTags = Object.values(tags);
    return allTags.filter(tag => tag.properties && tag.properties.includes(propertyId));
  };
  
  const [properties, setProperties] = useState(savedData.properties);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(savedData.totalCount);
  const [filters, setFilters] = useState(savedData.filters);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  
  // Tag management state
  const [propertyToTag, setPropertyToTag] = useState(null);
  
  const openTagManager = (property) => {
    console.log('openTagManager called with:', property);
    setPropertyToTag(property);
  };
  
  const closeTagManager = () => {
    setPropertyToTag(null);
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalProperties, setTotalProperties] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Routing state
  const [currentView, setCurrentView] = useState('search'); // 'search', 'detail', 'favorites', or 'collections'
  const [selectedProperty, setSelectedProperty] = useState(null);

  // Search mode state
  const [searchMode, setSearchMode] = useState('address'); // 'address' or 'filters'

  // Address search state
  const [addressSearch, setAddressSearch] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const [addressSearchPerformed, setAddressSearchPerformed] = useState(false);
  const [searchedAddress, setSearchedAddress] = useState('');

  // Check for migration prompt after authentication
  useEffect(() => {
    if (auth.initialized && auth.isAuthenticated && auth.hasMigratableData()) {
      setShowMigrationPrompt(true);
    }
  }, [auth.initialized, auth.isAuthenticated]);

  // Authentication handlers
  const handleSignInClick = () => {
    setShowSignInModal(true);
  };

  const handleMigration = async () => {
    const migrated = await auth.migrateAnonymousData();
    if (migrated) {
      // Refresh page to reload data with authenticated user
      window.location.reload();
    }
  };

  const handleSkipMigration = () => {
    // Clear migration flag
    sessionStorage.removeItem('migrate_from_user');
  };

  // Enhanced toggle favorite with auth check
  const handleToggleFavorite = (propertyId) => {
    if (auth.isAnonymous && !showSignInModal) {
      setShowSignInModal(true);
      return;
    }
    toggleFavorite(propertyId);
  };

  // Enhanced tag operations with auth check
  const handleOpenTagManager = (property) => {
    if (auth.isAnonymous && !showSignInModal) {
      setShowSignInModal(true);
      return;
    }
    openTagManager(property);
  };

  // Navigation functions
  const navigateToProperty = (property) => {
    setSelectedProperty(property);
    setCurrentView('detail');
    window.history.pushState({ view: 'detail', rollNumber: property.roll_number }, '', `#/property/${property.roll_number}`);
  };

  const navigateToSearch = () => {
    setCurrentView('search');
    setSelectedProperty(null);
    window.history.pushState({ view: 'search' }, '', '#/');
  };

  const navigateToFavorites = () => {
    setCurrentView('favorites');
    setSelectedProperty(null);
    window.history.pushState({ view: 'favorites' }, '', '#/favorites');
  };

  const navigateToCollections = () => {
    setCurrentView('collections');
    setSelectedProperty(null);
    window.history.pushState({ view: 'collections' }, '', '#/collections');
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = async (event) => {
      const hash = window.location.hash;
      if (hash.startsWith('#/property/')) {
        const rollNumber = hash.split('/')[2];
        let property = properties.find(p => p.roll_number === rollNumber);
        
        if (!property) {
          // Property not in current results, try to fetch it from API
          try {
            const query = `${API_BASE_URL}?$where=${encodeURIComponent(`roll_number = '${rollNumber}'`)}&$limit=1`;
            const response = await fetch(query);
            if (response.ok) {
              const data = await response.json();
              if (data && data.length > 0) {
                property = data[0];
              }
            }
          } catch (error) {
            console.error('Error fetching property:', error);
          }
        }
        
        if (property) {
          setSelectedProperty(property);
          setCurrentView('detail');
        } else {
          // Property not found, go back to search
          navigateToSearch();
        }
      } else if (hash === '#/favorites') {
        setCurrentView('favorites');
        setSelectedProperty(null);
      } else if (hash === '#/collections') {
        setCurrentView('collections');
        setSelectedProperty(null);
      } else {
        navigateToSearch();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Handle initial load with hash
    const handleInitialHash = async () => {
      const initialHash = window.location.hash;
      if (initialHash.startsWith('#/property/')) {
        const rollNumber = initialHash.split('/')[2];
        let property = properties.find(p => p.roll_number === rollNumber);
        
        if (!property) {
          // Property not in current results, try to fetch it from API
          try {
            const query = `${API_BASE_URL}?$where=${encodeURIComponent(`roll_number = '${rollNumber}'`)}&$limit=1`;
            const response = await fetch(query);
            if (response.ok) {
              const data = await response.json();
              if (data && data.length > 0) {
                property = data[0];
              }
            }
          } catch (error) {
            console.error('Error fetching property:', error);
          }
        }
        
        if (property) {
          setSelectedProperty(property);
          setCurrentView('detail');
        }
      } else if (initialHash === '#/favorites') {
        setCurrentView('favorites');
      } else if (initialHash === '#/collections') {
        setCurrentView('collections');
      }
    };
    
    handleInitialHash();

    return () => window.removeEventListener('popstate', handlePopState);
  }, [properties]);

  // Address search function with smart matching
  const handleAddressSearch = async () => {
    if (!addressSearch.trim()) return;
    
    const searchTerm = addressSearch.trim().toUpperCase();
    
    setAddressSearchLoading(true);
    setAddressResults([]);
    setSearchedAddress(searchTerm);
    setAddressSearchPerformed(true);
    
    try {
      
      // Create parallel search strategies for better performance
      const parallelStrategies = [
        // 1. Exact full address match (highest priority)
        {
          name: 'Exact Match',
          condition: `full_address = '${searchTerm}'`,
          priority: 1
        },
        
        // 2. Full address contains (for partial matches)
        {
          name: 'Full Address Contains',
          condition: `full_address LIKE '%${searchTerm}%'`,
          priority: 2
        },
        
        // 3. Parse and match components if it looks like "123 Main Street"
        ...(searchTerm.match(/^\d+\s+(.+)/) ? [{
          name: 'Street Number + Name',
          condition: (() => {
            const match = searchTerm.match(/^(\d+)\s+(.+)$/);
            const streetNumber = match[1];
            const streetPart = match[2];
            return `street_number = '${streetNumber}' AND (street_name LIKE '%${streetPart}%' OR full_address LIKE '%${streetPart}%')`;
          })(),
          priority: 3
        }] : []),
        
        // 4. Street name only match
        {
          name: 'Street Name Only',
          condition: `street_name LIKE '%${searchTerm}%'`,
          priority: 4
        }
      ];

      // Execute all search strategies in parallel
      const searchPromises = parallelStrategies.map(async (strategy) => {
        const query = `${API_BASE_URL}?$limit=50&$where=${encodeURIComponent(strategy.condition)}&$order=full_address ASC`;
        
        try {
          const response = await fetch(query);
          
          if (!response.ok) {
            return { strategy, results: [], error: response.status };
          }
          
          const data = await response.json();
          
          return { 
            strategy, 
            results: data
          };
        } catch (error) {
          return { strategy, results: [], error: error.message };
        }
      });

      // Race condition - take first successful result
      let bestResult = null;
      let raceFinished = false;
      
      const racePromise = new Promise((resolve) => {
        searchPromises.forEach((promise) => {
          promise.then(result => {
            if (!raceFinished && result.results && result.results.length > 0) {
              // Take the first successful result
              if (!bestResult) {
                bestResult = result;
                raceFinished = true;
                resolve();
                return;
              } else if (result.strategy.priority < bestResult.strategy.priority) {
                bestResult = result;
              }
            }
          }).catch(() => {
            // Ignore errors, continue with other strategies
          });
        });
        
        // Fallback: wait for all to complete if no early termination
        Promise.allSettled(searchPromises).then(() => resolve());
      });
      
      await racePromise;
      
      let results = [];
      if (bestResult) {
        results = bestResult.results;
      }
      
      // Remove duplicates based on roll_number (in case of overlapping results)
      const uniqueResults = results.filter((property, index, self) => 
        index === self.findIndex(p => p.roll_number === property.roll_number)
      );
      
      setAddressResults(uniqueResults);
      
    } catch (err) {
      console.error('Address search error:', err);
      setAddressResults([]);
    } finally {
      setAddressSearchLoading(false);
    }
  };

  // Handle search mode change
  const handleSearchModeChange = (newMode) => {
    if (newMode === searchMode) return; // Already in this mode

    setSearchMode(newMode);

    // Clear results from other modes for cleaner interface
    if (newMode === 'address') {
      // Switching to address mode - clear filter and sales results
      setProperties([]);
      setTotalCount(null);
      setError('');
      setCurrentPage(0);
      setTotalProperties(null);
      setHasMore(false);
      setSalesProperties([]);
      setSalesSearchPerformed(false);
      clearSalesResults();
    } else if (newMode === 'filters') {
      // Switching to filters mode - clear address and sales results
      setAddressResults([]);
      setAddressSearchPerformed(false);
      setSearchedAddress('');
      setAddressSearch('');
      setSalesProperties([]);
      setSalesSearchPerformed(false);
      clearSalesResults();
    } else if (newMode === 'sales') {
      // Switching to sales mode - clear address and filter results
      setAddressResults([]);
      setAddressSearchPerformed(false);
      setSearchedAddress('');
      setAddressSearch('');
      setProperties([]);
      setTotalCount(null);
      setError('');
      setCurrentPage(0);
      setTotalProperties(null);
      setHasMore(false);
    }
  };

  // Handle sales search
  const handleSalesSearch = async () => {
    setSalesSearchPerformed(true);
    setSalesProperties([]);

    const result = await searchSales(filters);

    if (result && result.results && result.results.length > 0) {
      // Get roll numbers from sales results
      const rollNumbers = result.results.map(sale => sale.roll_number);

      // Fetch full property details from Winnipeg API
      const properties = await fetchPropertiesFromRollNumbers(rollNumbers);

      // Merge sales info with property data
      const propertiesWithSales = properties.map(property => {
        const saleInfo = result.results.find(s => s.roll_number === property.roll_number);
        return {
          ...property,
          _saleInfo: saleInfo
        };
      });

      setSalesProperties(propertiesWithSales);
    }
  };

  // Load more sales results
  const handleLoadMoreSales = async () => {
    setSalesLoadingMore(true);
    try {
      const offset = salesProperties.length;
      const result = await searchSales(filters, offset);

      if (result && result.results && result.results.length > 0) {
        const rollNumbers = result.results.map(sale => sale.roll_number);
        const properties = await fetchPropertiesFromRollNumbers(rollNumbers);

        const propertiesWithSales = properties.map(property => {
          const saleInfo = result.results.find(s => s.roll_number === property.roll_number);
          return {
            ...property,
            _saleInfo: saleInfo
          };
        });

        setSalesProperties(prev => [...prev, ...propertiesWithSales]);
      }
    } finally {
      setSalesLoadingMore(false);
    }
  };

  // Clear sales search
  const clearSalesSearch = () => {
    setSalesProperties([]);
    setSalesSearchPerformed(false);
    clearSalesResults();
    handleFilterChange('minSalePrice', '');
    handleFilterChange('maxSalePrice', '');
    handleFilterChange('minSaleYear', '');
    handleFilterChange('maxSaleYear', '');
    handleFilterChange('salesBuildingType', '');
    handleFilterChange('salesAddress', '');
  };

  // Toggle favorite property
  // Set up global function reference for map popups
  useEffect(() => {
    window.toggleFavoriteFunction = toggleFavorite;
    return () => {
      window.toggleFavoriteFunction = null;
    };
  }, [toggleFavorite]);

  // Clean up stale selectedTags that no longer exist
  useEffect(() => {
    if (!tagsLoading && filters.selectedTags && filters.selectedTags.length > 0) {
      const validTagIds = Object.keys(tags);
      const validSelectedTags = filters.selectedTags.filter(id => validTagIds.includes(id));
      if (validSelectedTags.length !== filters.selectedTags.length) {
        setFilters(prev => ({ ...prev, selectedTags: validSelectedTags }));
      }
    }
  }, [tagsLoading, tags]);

  const buildQuery = () => {
    const conditions = [];
    
    if (filters.streetName) {
      conditions.push(`street_name LIKE '%${filters.streetName.toUpperCase()}%'`);
    }
    
    if (filters.minYear) {
      conditions.push(`year_built >= ${filters.minYear}`);
    }
    
    if (filters.maxYear) {
      conditions.push(`year_built <= ${filters.maxYear}`);
    }
    
    if (filters.minArea) {
      conditions.push(`total_living_area >= ${filters.minArea}`);
    }
    
    if (filters.maxArea) {
      conditions.push(`total_living_area <= ${filters.maxArea}`);
    }
    
    if (filters.minValue) {
      conditions.push(`total_assessed_value >= ${filters.minValue}`);
    }
    
    if (filters.maxValue) {
      conditions.push(`total_assessed_value <= ${filters.maxValue}`);
    }
    
    if (filters.propertyType) {
      conditions.push(`property_use_code LIKE '%${filters.propertyType}%'`);
    }

    if (filters.buildingType) {
      conditions.push(`building_type = '${filters.buildingType}'`);
    }

    if (filters.basement === 'Yes' || filters.basement === 'No') {
      conditions.push(`basement = '${filters.basement}'`);
    }

    if (filters.garage === 'Yes') {
      conditions.push(`(attached_garage = 'Yes' OR detached_garage = 'Yes')`);
    } else if (filters.garage === 'No') {
      conditions.push(`(attached_garage = 'No' AND detached_garage = 'No')`);
    }

    if (filters.pool === 'Yes' || filters.pool === 'No') {
      conditions.push(`pool = '${filters.pool}'`);
    }

    if (filters.airConditioning === 'Yes' || filters.airConditioning === 'No') {
      conditions.push(`air_conditioning = '${filters.airConditioning}'`);
    }

    if (filters.riverAdjacent) {
      conditions.push(`property_influences LIKE '%RIVER ADJACENT%'`);
    }

    let query = `${API_BASE_URL}?$limit=250&$order=total_assessed_value DESC`;
    
    if (conditions.length > 0) {
      query += `&$where=${encodeURIComponent(conditions.join(' AND '))}`;
    }
    
    return query;
  };

  // Get total count of properties matching filters
  const fetchTotalCount = async () => {
    const conditions = [];
    
    if (filters.streetName) {
      conditions.push(`street_name LIKE '%${filters.streetName.toUpperCase()}%'`);
    }
    
    if (filters.minYear) {
      conditions.push(`year_built >= ${filters.minYear}`);
    }
    
    if (filters.maxYear) {
      conditions.push(`year_built <= ${filters.maxYear}`);
    }
    
    if (filters.minArea) {
      conditions.push(`total_living_area >= ${filters.minArea}`);
    }
    
    if (filters.maxArea) {
      conditions.push(`total_living_area <= ${filters.maxArea}`);
    }
    
    if (filters.minValue) {
      conditions.push(`total_assessed_value >= ${filters.minValue}`);
    }
    
    if (filters.maxValue) {
      conditions.push(`total_assessed_value <= ${filters.maxValue}`);
    }
    
    if (filters.propertyType) {
      conditions.push(`property_use_code LIKE '%${filters.propertyType}%'`);
    }

    if (filters.buildingType) {
      conditions.push(`building_type = '${filters.buildingType}'`);
    }

    if (filters.basement === 'Yes' || filters.basement === 'No') {
      conditions.push(`basement = '${filters.basement}'`);
    }

    if (filters.garage === 'Yes') {
      conditions.push(`(attached_garage = 'Yes' OR detached_garage = 'Yes')`);
    } else if (filters.garage === 'No') {
      conditions.push(`(attached_garage = 'No' AND detached_garage = 'No')`);
    }

    if (filters.pool === 'Yes' || filters.pool === 'No') {
      conditions.push(`pool = '${filters.pool}'`);
    }

    if (filters.airConditioning === 'Yes' || filters.airConditioning === 'No') {
      conditions.push(`air_conditioning = '${filters.airConditioning}'`);
    }

    if (filters.riverAdjacent) {
      conditions.push(`property_influences LIKE '%RIVER ADJACENT%'`);
    }

    let countQuery = `${API_BASE_URL}?$select=count(*)`;
    
    if (conditions.length > 0) {
      countQuery += `&$where=${encodeURIComponent(conditions.join(' AND '))}`;
    }

    try {
      const response = await fetch(countQuery);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const total = parseInt(data[0].count);
      setTotalProperties(total);
      return total;
    } catch (err) {
      console.error('Error fetching total count:', err);
      return null;
    }
  };

  const fetchAllProperties = async () => {
    setLoading(true);
    setCurrentPage(0);
    setProperties([]);
    setTotalProperties(null);
    setError('');
    
    try {
      // Get total count first
      const total = await fetchTotalCount();
      
      if (!total) {
        setError('Unable to get total count of properties');
        return;
      }
      
      console.log(`Loading all ${total} properties...`);
      
      // Calculate how many requests we need (250 properties per request)
      const totalPages = Math.ceil(total / 250);
      let allProperties = [];
      
      for (let page = 0; page < totalPages; page++) {
        const offset = page * 250;
        const query = buildQuery() + `&$offset=${offset}`;
        console.log(`Loading page ${page + 1} of ${totalPages}...`);
        
        const response = await fetch(query);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        allProperties = [...allProperties, ...data];
        
        // Update progress
        setProperties([...allProperties]);
        setTotalCount(allProperties.length);
      }
      
      setCurrentPage(totalPages);
      setHasMore(false); // All properties loaded
      
      // Save to localStorage
      try {
        localStorage.setItem('winnipegPropertyFilters', JSON.stringify(filters));
        localStorage.setItem('winnipegPropertyResults', JSON.stringify(allProperties));
        localStorage.setItem('winnipegPropertyTotalCount', JSON.stringify(allProperties.length));
      } catch (err) {
        console.error('Error saving to localStorage:', err);
      }
      
      console.log(`Successfully loaded all ${allProperties.length} properties`);
      
    } catch (err) {
      console.error('Error fetching all properties:', err);
      setError('Failed to fetch all properties. You can try loading them in smaller chunks with the regular search.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async (isNewSearch = true) => {
    if (isNewSearch) {
      setLoading(true);
      setCurrentPage(0);
      setProperties([]);
      setTotalProperties(null);
    } else {
      setLoadingMore(true);
    }
    
    setError('');
    
    try {
      // Get total count for new searches
      let total = totalProperties;
      if (isNewSearch) {
        total = await fetchTotalCount();
      }
      
      const offset = isNewSearch ? 0 : currentPage * 250;
      const query = buildQuery() + `&$offset=${offset}`;
      console.log('API Query:', query);
      
      const response = await fetch(query);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (isNewSearch) {
        setProperties(data);
        setCurrentPage(1);
      } else {
        setProperties(prev => [...prev, ...data]);
        setCurrentPage(prev => prev + 1);
      }
      
      setTotalCount(isNewSearch ? data.length : properties.length + data.length);
      
      // Check if there are more properties to load
      const currentTotal = isNewSearch ? data.length : properties.length + data.length;
      const hasMoreProperties = total && total > currentTotal;
      console.log('Pagination Debug:', {
        total,
        currentTotal,
        hasMoreProperties,
        dataLength: data.length,
        propertiesLength: properties.length
      });
      setHasMore(hasMoreProperties);
      
      // Save to localStorage
      try {
        const finalProperties = isNewSearch ? data : [...properties, ...data];
        localStorage.setItem('winnipegPropertyFilters', JSON.stringify(filters));
        localStorage.setItem('winnipegPropertyResults', JSON.stringify(finalProperties));
        localStorage.setItem('winnipegPropertyTotalCount', JSON.stringify(finalProperties.length));
      } catch (err) {
        console.error('Error saving to localStorage:', err);
      }
      
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError('Failed to fetch property data. This may be due to CORS restrictions or network issues.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const clearFilters = () => {
    const defaultFilters = {
      streetName: '',
      minYear: '',
      maxYear: '',
      minArea: '',
      maxArea: '',
      minValue: '',
      maxValue: '',
      propertyType: '',
      buildingType: '',
      basement: '',
      garage: '',
      pool: '',
      airConditioning: '',
      riverAdjacent: false,
      favoritesOnly: false,
      selectedTags: [],
      minSalePrice: '',
      maxSalePrice: '',
      minSaleYear: '',
      maxSaleYear: '',
      salesBuildingType: '',
      salesAddress: ''
    };

    setFilters(defaultFilters);
    setProperties([]);
    setTotalCount(null);
    setError('');

    // Reset pagination state
    setCurrentPage(0);
    setTotalProperties(null);
    setHasMore(false);
    setLoadingMore(false);

    // Clear localStorage
    try {
      localStorage.removeItem('winnipegPropertyFilters');
      localStorage.removeItem('winnipegPropertyResults');
      localStorage.removeItem('winnipegPropertyTotalCount');
    } catch (err) {
      console.error('Error clearing localStorage:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter properties to show favorites only if filter is active, and by selected tags
  const getFilteredProperties = () => {
    let filtered = properties;
    
    if (filters.favoritesOnly) {
      filtered = filtered.filter(property => isFavorite(property.roll_number));
    }
    
    if (filters.selectedTags && filters.selectedTags.length > 0) {
      filtered = filtered.filter(property => {
        const propertyTags = getPropertyTags(property.roll_number);
        return filters.selectedTags.some(selectedTagId => 
          propertyTags.some(propertyTag => propertyTag.id === selectedTagId)
        );
      });
    }
    
    return filtered;
  };

  // Render the appropriate view
  if (currentView === 'detail') {
    return (
      <div className="min-h-screen p-4">
        <PropertyDetail
          property={selectedProperty}
          onBack={navigateToSearch}
          isFavorite={isFavorite}
          toggleFavorite={handleToggleFavorite}
          getPropertyTags={getPropertyTags}
          onOpenTagManager={handleOpenTagManager}
          getNote={getNote}
          saveNote={saveNote}
          deleteNote={deleteNote}
        />
        
        {/* Tag Management Dialog */}
        <PropertyTagManager 
          property={propertyToTag}
          isOpen={!!propertyToTag}
          onClose={closeTagManager}
          getPropertyTags={getPropertyTags}
          getAllTags={getAllTags}
          addPropertyToTag={addPropertyToTag}
          removePropertyFromTag={removePropertyFromTag}
          createNewTag={createNewTag}
          updateTag={updateTag}
          deleteTag={deleteTag}
        />
      </div>
    );
  }

  if (currentView === 'favorites') {
    return (
      <div>
        <FavoritesView
          favorites={favorites}
          properties={properties}
          onPropertyClick={navigateToProperty}
          toggleFavorite={handleToggleFavorite}
          isFavorite={isFavorite}
          onBack={navigateToSearch}
          onClearAll={clearAllFavorites}
          getPropertyTags={getPropertyTags}
          onOpenTagManager={handleOpenTagManager}
          hasNote={hasNote}
        />
        
        {/* Tag Management Dialog */}
        <PropertyTagManager 
          property={propertyToTag}
          isOpen={!!propertyToTag}
          onClose={closeTagManager}
          getPropertyTags={getPropertyTags}
          getAllTags={getAllTags}
          addPropertyToTag={addPropertyToTag}
          removePropertyFromTag={removePropertyFromTag}
          createNewTag={createNewTag}
          updateTag={updateTag}
          deleteTag={deleteTag}
        />
      </div>
    );
  }

  if (currentView === 'collections') {
    return (
      <div>
        <CollectionsView
          properties={properties}
          onPropertyClick={navigateToProperty}
          toggleFavorite={handleToggleFavorite}
          isFavorite={isFavorite}
          onBack={navigateToSearch}
          getAllTags={getAllTags}
          getPropertyTags={getPropertyTags}
          onOpenTagManager={handleOpenTagManager}
          updateTag={updateTag}
          deleteTag={deleteTag}
          hasNote={hasNote}
        />
        
        {/* Tag Management Dialog */}
        <PropertyTagManager 
          property={propertyToTag}
          isOpen={!!propertyToTag}
          onClose={closeTagManager}
          getPropertyTags={getPropertyTags}
          getAllTags={getAllTags}
          addPropertyToTag={addPropertyToTag}
          removePropertyFromTag={removePropertyFromTag}
          createNewTag={createNewTag}
          updateTag={updateTag}
          deleteTag={deleteTag}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          {/* Navigation - Top Right */}
          <div className="flex justify-end items-center gap-3 mb-8">
            <Button
              onClick={navigateToCollections}
              variant="outline"
              size="lg"
              className="flex items-center gap-2"
            >
              <TagIcon />
              Collections
              {(() => {
                const tagCount = getAllTags().length;
                return tagCount > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full ml-1">
                    {tagCount}
                  </span>
                );
              })()}
            </Button>
            <Button
              onClick={navigateToFavorites}
              variant="outline"
              size="lg"
              className="flex items-center gap-2"
            >
              <HeartIcon filled={favorites.length > 0} className={`w-5 h-5 ${favorites.length > 0 ? 'text-red-500' : ''}`} />
              My Favorites
              {favorites.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-1">
                  {favorites.length}
                </span>
              )}
            </Button>
            
            {/* User Profile / Sign In */}
            <UserProfileDropdown
              user={auth.user}
              onSignOut={auth.signOut}
              onSignIn={handleSignInClick}
            />
          </div>
          
          {/* Title Section */}
          <div className="text-center">
            <h1 className="text-5xl font-semibold text-foreground mb-4 text-display">
              Winnipeg Property Explorer
            </h1>
            <p className="text-muted-foreground text-xl max-w-2xl mx-auto text-body">
              Search and explore residential property data from the City of Winnipeg
            </p>
          </div>
        </div>

        {/* Search Mode Toggle */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="text-sm text-muted-foreground mr-2">Search Mode:</span>
          <Button
            onClick={() => handleSearchModeChange('address')}
            variant={searchMode === 'address' ? 'default' : 'ghost'}
            size="sm"
            className="flex items-center"
          >
            <SearchIcon className="w-4 h-4 mr-2" />
            Address Search
          </Button>
          <Button
            onClick={() => handleSearchModeChange('filters')}
            variant={searchMode === 'filters' ? 'default' : 'ghost'}
            size="sm"
            className="flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-2">
              <polygon points="14 2 18 6 7 17 3 17 3 13 14 2"></polygon>
              <line x1="16" y1="8" x2="2" y2="22"></line>
              <line x1="23" y1="1" x2="17" y2="7"></line>
            </svg>
            Advanced Filters
          </Button>
          <Button
            onClick={() => handleSearchModeChange('sales')}
            variant={searchMode === 'sales' ? 'default' : 'ghost'}
            size="sm"
            className="flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            Sales History Search
          </Button>
        </div>

        {/* Search Section */}
        {searchMode === 'sales' ? (
          <div>
            {/* Sales History Search */}
            <Card className="mb-8 shadow-sm">
              <CardHeader className="pb-6">
                <CardTitle className="flex items-center text-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <span className="ml-3">Sales History Search</span>
                </CardTitle>
                <CardDescription>
                  Find properties based on their historical sale prices and dates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sale Price Range */}
                <div className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel htmlFor="min-sale-price">Min Sale Price</FieldLabel>
                      <Input
                        id="min-sale-price"
                        type="number"
                        value={filters.minSalePrice}
                        onChange={(e) => handleFilterChange('minSalePrice', e.target.value)}
                        placeholder="e.g., 200000"
                        className="h-11"
                      />
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="max-sale-price">Max Sale Price</FieldLabel>
                      <Input
                        id="max-sale-price"
                        type="number"
                        value={filters.maxSalePrice}
                        onChange={(e) => handleFilterChange('maxSalePrice', e.target.value)}
                        placeholder="e.g., 500000"
                        className="h-11"
                      />
                    </Field>
                  </div>
                </div>

                {/* Sale Year Range */}
                <div className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel htmlFor="min-sale-year">Min Sale Year</FieldLabel>
                      <Input
                        id="min-sale-year"
                        type="number"
                        value={filters.minSaleYear}
                        onChange={(e) => handleFilterChange('minSaleYear', e.target.value)}
                        placeholder="e.g., 2020"
                        className="h-11"
                      />
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="max-sale-year">Max Sale Year</FieldLabel>
                      <Input
                        id="max-sale-year"
                        type="number"
                        value={filters.maxSaleYear}
                        onChange={(e) => handleFilterChange('maxSaleYear', e.target.value)}
                        placeholder="e.g., 2024"
                        className="h-11"
                      />
                    </Field>
                  </div>
                </div>

                {/* Building Type and Address */}
                <div className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel htmlFor="sales-building-type">Building Type</FieldLabel>
                      <Select
                        value={filters.salesBuildingType}
                        onValueChange={(value) => handleFilterChange('salesBuildingType', value)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Any</SelectItem>
                          <SelectItem value="ONE STOREY">One Storey</SelectItem>
                          <SelectItem value="TWO STOREY">Two Storey</SelectItem>
                          <SelectItem value="SPLIT LEVEL">Split Level</SelectItem>
                          <SelectItem value="BI-LEVEL">Bi-Level</SelectItem>
                          <SelectItem value="TRI-LEVEL">Tri-Level</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="sales-address">Address Contains</FieldLabel>
                      <Input
                        id="sales-address"
                        type="text"
                        value={filters.salesAddress}
                        onChange={(e) => handleFilterChange('salesAddress', e.target.value)}
                        placeholder="e.g., Pembina"
                        className="h-11"
                      />
                    </Field>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Button
                      onClick={handleSalesSearch}
                      disabled={salesLoading || (!filters.minSalePrice && !filters.maxSalePrice && !filters.minSaleYear && !filters.maxSaleYear && !filters.salesBuildingType && !filters.salesAddress)}
                      className="flex items-center gap-2 h-11 px-6"
                    >
                      {salesLoading ? (
                        <>
                          <LoaderIcon className="w-4 h-4" />
                          <span>Searching...</span>
                        </>
                      ) : (
                        <>
                          <SearchIcon className="w-4 h-4" />
                          <span>Search Sales History</span>
                        </>
                      )}
                    </Button>

                    <Button
                      onClick={clearSalesSearch}
                      disabled={salesLoading}
                      variant="outline"
                      className="h-11 px-4"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sales Search Error */}
            {salesError && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircleIcon />
                <AlertDescription>{salesError}</AlertDescription>
              </Alert>
            )}

            {/* Sales Search Results */}
            {salesSearchPerformed && (salesLoading || salesProperties.length > 0 || salesTotalCount !== null) && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Sales Search Results</span>
                    {salesTotalCount !== null && (
                      <span className="text-sm font-normal text-muted-foreground">
                        Found {salesTotalCount} properties with matching sales
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {salesLoading || (salesProperties.length === 0 && salesTotalCount > 0) ? (
                    <div className="text-center py-12">
                      <LoaderIcon className="w-12 h-12 mx-auto mb-4 block animate-spin" />
                      <p className="text-muted-foreground">Searching sales history...</p>
                    </div>
                  ) : salesProperties.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 text-gray-300 mx-auto mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                      </div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2">No Sales Found</h3>
                      <p className="text-gray-500 mb-4">
                        No properties found with sales matching your criteria.
                      </p>
                      <div className="text-sm text-gray-600">
                        <p className="mb-2">Try:</p>
                        <ul className="text-left max-w-md mx-auto space-y-1">
                          <li>- Expanding the price range</li>
                          <li>- Expanding the year range</li>
                          <li>- Removing the building type filter</li>
                          <li>- Using a more general address search</li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-muted-foreground">
                          Showing {salesProperties.length} properties with matching sales history:
                        </p>
                        {/* View Mode Toggle */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground mr-2">View:</span>
                          <Button
                            onClick={() => setSalesViewMode('grid')}
                            variant={salesViewMode === 'grid' ? 'default' : 'ghost'}
                            size="sm"
                            className="flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                              <rect x="3" y="3" width="7" height="7"></rect>
                              <rect x="14" y="3" width="7" height="7"></rect>
                              <rect x="14" y="14" width="7" height="7"></rect>
                              <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                            Cards
                          </Button>
                          <Button
                            onClick={() => setSalesViewMode('map')}
                            variant={salesViewMode === 'map' ? 'default' : 'ghost'}
                            size="sm"
                            className="flex items-center"
                          >
                            <MapIcon />
                            <span className="ml-1">Map</span>
                          </Button>
                        </div>
                      </div>

                      {salesViewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {salesProperties.map((property) => (
                            <div key={property.roll_number} className="relative">
                              <PropertyCard
                                property={property}
                                onPropertyClick={navigateToProperty}
                                toggleFavorite={handleToggleFavorite}
                                isFavorite={isFavorite}
                                getPropertyTags={getPropertyTags}
                                onOpenTagManager={handleOpenTagManager}
                                hasNote={hasNote}
                              />
                              {/* Sale info overlay */}
                              {property._saleInfo && (
                                <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full shadow-md">
                                  ${property._saleInfo.sale_price?.toLocaleString()} ({property._saleInfo.sale_year})
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center">
                              <MapIcon />
                              <span className="ml-2">Sales Property Locations</span>
                              <span className="ml-2 text-sm text-gray-500 font-normal">
                                ({salesProperties.filter(p => p.centroid_lat && p.centroid_lon).length} properties with coordinates)
                              </span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <PropertyMap
                              properties={salesProperties}
                              isFavorite={isFavorite}
                              favoritesState={favorites}
                              onPropertyClick={navigateToProperty}
                            />
                            <p className="text-xs text-gray-500 mt-3">
                              Click on map markers to see property details. Some properties may not appear if coordinates are unavailable.
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Sales Load More Button */}
                  {salesProperties.length > 0 && salesProperties.length < salesTotalCount && !salesLoading && (
                    <div className="text-center my-6">
                      <Button
                        onClick={handleLoadMoreSales}
                        disabled={salesLoadingMore}
                        size="lg"
                        className="flex items-center mx-auto"
                      >
                        {salesLoadingMore ? (
                          <>
                            <LoaderIcon />
                            <span className="ml-2">Loading More Properties...</span>
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                              <path d="M12 5v14"></path>
                              <path d="M5 12h14"></path>
                            </svg>
                            Load More Properties ({Math.min(250, salesTotalCount - salesProperties.length)} more available)
                          </>
                        )}
                      </Button>
                      <p className="text-sm text-gray-500 mt-2">
                        Loading 250 properties at a time for better performance
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        ) : searchMode === 'address' ? (
          <div>
            {/* Address Search Bar */}
            <Card className="mb-8 shadow-lg border-2 border-primary/20">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Find a Specific Property
              </h2>
              <p className="text-muted-foreground">
                Search by full address to find detailed property information
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <div className="flex gap-3">
                <InputGroup className="flex-1 h-14">
                  <InputGroupAddon>
                    <SearchIcon className="w-5 h-5" />
                  </InputGroupAddon>
                  <InputGroupInput
                    type="text"
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddressSearch()}
                    placeholder="Enter full address (e.g., 123 Main Street)"
                    className="text-lg"
                  />
                  {addressSearch && (
                    <InputGroupAddon align="inline-end">
                      <button
                        onClick={() => {
                          setAddressSearch('');
                          setAddressResults([]);
                          setAddressSearchPerformed(false);
                        }}
                        className="text-muted-foreground hover:text-foreground transition-colors p-1"
                      >
                        <XIcon className="w-5 h-5" />
                      </button>
                    </InputGroupAddon>
                  )}
                </InputGroup>
                
                <Button
                  onClick={handleAddressSearch}
                  disabled={!addressSearch.trim() || addressSearchLoading}
                  size="lg"
                  className="h-14 px-8 text-lg"
                >
                  {addressSearchLoading ? (
                    <>
                      <LoaderIcon className="w-5 h-5 mr-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <SearchIcon className="w-5 h-5 mr-2" />
                      Search Property
                    </>
                  )}
                </Button>
              </div>
              
              <div className="mt-4 text-center text-sm text-muted-foreground">
                Try formats like: "123 Main Street", "456 Portage Ave", or just "Oak Street"
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Search Results */}
        {addressSearchPerformed && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Search Results for "{searchedAddress}"</span>
                <Button
                  onClick={() => {
                    setAddressResults([]);
                    setAddressSearchPerformed(false);
                    setSearchedAddress('');
                  }}
                  variant="ghost"
                  size="sm"
                >
                  <XIcon className="w-4 h-4 mr-1" />
                  Clear Results
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {addressSearchLoading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 text-primary mx-auto mb-4">
                    <LoaderIcon className="w-16 h-16" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Searching Properties...</h3>
                  <p className="text-gray-500">
                    Looking for properties matching "{searchedAddress}"
                  </p>
                </div>
              ) : addressResults.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 text-gray-300 mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No Property Found</h3>
                  <p className="text-gray-500 mb-4">
                    We couldn't find a property matching "{searchedAddress}".
                  </p>
                  <div className="text-sm text-gray-600">
                    <p className="mb-2">Try:</p>
                    <ul className="text-left max-w-md mx-auto space-y-1">
                      <li>• Including the street number: "123 Main Street"</li>
                      <li>• Using the full street name: "Portage Avenue"</li>
                      <li>• Checking spelling and abbreviations</li>
                      <li>• Using the advanced filters below for broader search</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-muted-foreground mb-4">
                    Found {addressResults.length} {addressResults.length === 1 ? 'property' : 'properties'} matching your search:
                  </p>
                  <div className={`grid gap-6 ${addressResults.length === 1 ? 'max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                    {addressResults.map((property) => (
                      <PropertyCard
                        key={property.roll_number}
                        property={property}
                        onPropertyClick={navigateToProperty}
                        toggleFavorite={handleToggleFavorite}
                        isFavorite={isFavorite}
                        getPropertyTags={getPropertyTags}
                        onOpenTagManager={handleOpenTagManager}
                        hasNote={hasNote}
                      />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
          </div>
        ) : (
          <div>
            {/* Advanced Filters */}
            <Card className="mb-8 shadow-sm">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center text-xl">
              <SearchIcon className="w-5 h-5" />
              <span className="ml-3">Advanced Property Search</span>
            </CardTitle>
            <CardDescription>
              Search by property characteristics, features, and price range
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
          
          {/* Basic Search */}
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Street Name */}
              <Field>
                <FieldLabel htmlFor="street-name">Street Name</FieldLabel>
                <Input
                  id="street-name"
                  type="text"
                  value={filters.streetName}
                  onChange={(e) => handleFilterChange('streetName', e.target.value)}
                  placeholder="e.g., Pembina"
                  className="h-11"
                />
              </Field>

              {/* Property Type */}
              <Field>
                <FieldLabel htmlFor="property-type">Property Type</FieldLabel>
                <Select
                  value={filters.propertyType}
                  onValueChange={(value) => handleFilterChange('propertyType', value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="RESSD">Detached Single Dwelling</SelectItem>
                    <SelectItem value="CONDOMINIUM">Condominium</SelectItem>
                    <SelectItem value="MULTI">Multi-Family</SelectItem>
                    <SelectItem value="VACANT">Vacant Land</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* Building Type (Stories) */}
              <Field>
                <FieldLabel htmlFor="building-type">Building Type</FieldLabel>
                <Select
                  value={filters.buildingType}
                  onValueChange={(value) => handleFilterChange('buildingType', value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="ONE STOREY">One Storey</SelectItem>
                    <SelectItem value="TWO STOREY">Two Storey</SelectItem>
                    <SelectItem value="SPLIT LEVEL">Split Level</SelectItem>
                    <SelectItem value="BI-LEVEL">Bi-Level</SelectItem>
                    <SelectItem value="TRI-LEVEL">Tri-Level</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          {/* Property Details */}
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Year Built Range */}
              <Field>
                <FieldLabel htmlFor="min-year">Min Year Built</FieldLabel>
                <Input
                  id="min-year"
                  type="number"
                  value={filters.minYear}
                  onChange={(e) => handleFilterChange('minYear', e.target.value)}
                  placeholder="e.g., 1990"
                  className="h-11"
                />
              </Field>
              
              <Field>
                <FieldLabel htmlFor="max-year">Max Year Built</FieldLabel>
                <Input
                  id="max-year"
                  type="number"
                  value={filters.maxYear}
                  onChange={(e) => handleFilterChange('maxYear', e.target.value)}
                  placeholder="e.g., 2020"
                  className="h-11"
                />
              </Field>

              {/* Living Area Range */}
              <Field>
                <FieldLabel htmlFor="min-area">Min Living Area (sq ft)</FieldLabel>
                <Input
                  id="min-area"
                  type="number"
                  value={filters.minArea}
                  onChange={(e) => handleFilterChange('minArea', e.target.value)}
                  placeholder="e.g., 1000"
                  className="h-11"
                />
              </Field>
              
              <Field>
                <FieldLabel htmlFor="max-area">Max Living Area (sq ft)</FieldLabel>
                <Input
                  id="max-area"
                  type="number"
                  value={filters.maxArea}
                  onChange={(e) => handleFilterChange('maxArea', e.target.value)}
                  placeholder="e.g., 3000"
                  className="h-11"
                />
              </Field>
            </div>
          </div>

          {/* Value Range */}
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="min-value">Min Assessed Value</FieldLabel>
                <Input
                  id="min-value"
                  type="number"
                  value={filters.minValue}
                  onChange={(e) => handleFilterChange('minValue', e.target.value)}
                  placeholder="e.g., 300000"
                  className="h-11"
                />
              </Field>
              
              <Field>
                <FieldLabel htmlFor="max-value">Max Assessed Value</FieldLabel>
                <Input
                  id="max-value"
                  type="number"
                  value={filters.maxValue}
                  onChange={(e) => handleFilterChange('maxValue', e.target.value)}
                  placeholder="e.g., 800000"
                  className="h-11"
                />
              </Field>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Basement */}
              <Field>
                <FieldLabel htmlFor="basement">Basement</FieldLabel>
                <Select
                  value={filters.basement}
                  onValueChange={(value) => handleFilterChange('basement', value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* Garage */}
              <Field>
                <FieldLabel htmlFor="garage">Garage</FieldLabel>
                <Select
                  value={filters.garage}
                  onValueChange={(value) => handleFilterChange('garage', value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* Pool */}
              <Field>
                <FieldLabel htmlFor="pool">Pool</FieldLabel>
                <Select
                  value={filters.pool}
                  onValueChange={(value) => handleFilterChange('pool', value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* Air Conditioning */}
              <Field>
                <FieldLabel htmlFor="air-conditioning">Air Conditioning</FieldLabel>
                <Select
                  value={filters.airConditioning}
                  onValueChange={(value) => handleFilterChange('airConditioning', value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            
            {/* River Adjacent */}
            <Field orientation="horizontal" className="pt-1">
              <input
                id="river-adjacent"
                type="checkbox"
                checked={filters.riverAdjacent}
                onChange={(e) => handleFilterChange('riverAdjacent', e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary focus:ring-ring focus:ring-offset-0"
              />
              <FieldLabel htmlFor="river-adjacent">River Adjacent Properties</FieldLabel>
            </Field>
            
            {/* Show Favorites Only */}
            <Field orientation="horizontal" className="pt-1">
              <input
                id="favorites-only"
                type="checkbox"
                checked={filters.favoritesOnly}
                onChange={(e) => handleFilterChange('favoritesOnly', e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary focus:ring-ring focus:ring-offset-0"
              />
              <FieldLabel htmlFor="favorites-only" className="flex items-center gap-1">
                <HeartIcon filled={true} className="w-4 h-4 text-red-500" />
                Show My Favorites Only ({favorites.length})
              </FieldLabel>
            </Field>
            
            {/* Tag Filtering */}
            {(() => {
              const allTags = getAllTags();
              if (allTags.length > 0) {
                return (
                  <div className="pt-2">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Filter by Tags</label>
                    <div className="flex flex-wrap gap-1">
                      {allTags.map(tag => (
                        <Badge
                          key={tag.id}
                          variant={filters.selectedTags && filters.selectedTags.includes(tag.id) ? "default" : "outline"}
                          className="cursor-pointer hover:bg-gray-50 text-xs"
                          style={filters.selectedTags && filters.selectedTags.includes(tag.id) 
                            ? { backgroundColor: tag.color, color: '#ffffff', border: 'none' }
                            : { borderColor: tag.color, color: tag.color }
                          }
                          onClick={() => {
                            const currentSelectedTags = filters.selectedTags || [];
                            const isSelected = currentSelectedTags.includes(tag.id);
                            const newSelectedTags = isSelected
                              ? currentSelectedTags.filter(id => id !== tag.id)
                              : [...currentSelectedTags, tag.id];
                            handleFilterChange('selectedTags', newSelectedTags);
                          }}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                      {filters.selectedTags && filters.selectedTags.length > 0 && (
                        <button
                          onClick={() => handleFilterChange('selectedTags', [])}
                          className="text-xs text-gray-500 hover:text-gray-700 ml-1"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="flex gap-3">
                <Button
                  onClick={fetchProperties}
                  disabled={loading}
                  className="flex items-center gap-2 h-11 px-6"
                >
                  {loading ? (
                    <>
                      <LoaderIcon className="w-4 h-4" />
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <SearchIcon className="w-4 h-4" />
                      <span>Search Properties (First 250)</span>
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={fetchAllProperties}
                  disabled={loading}
                  variant="secondary"
                  className="flex items-center gap-2 h-11 px-6"
                >
                  {loading ? (
                    <>
                      <LoaderIcon className="w-4 h-4" />
                      <span>Loading All...</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      <span>Load All Properties</span>
                    </>
                  )}
                </Button>
              </div>
              
              <Button
                onClick={clearFilters}
                disabled={loading}
                variant="outline"
                className="h-11 px-4"
              >
                Clear All
              </Button>
            </div>
          </div>
          </CardContent>
        </Card>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircleIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Results Summary & View Toggle */}
        {searchMode === 'filters' && totalCount !== null && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-foreground">
                  Showing {(filters.favoritesOnly || (filters.selectedTags && filters.selectedTags.length > 0)) ? getFilteredProperties().length : properties.length}
                  {filters.favoritesOnly
                    ? ' favorite properties'
                    : (filters.selectedTags && filters.selectedTags.length > 0)
                      ? ' filtered properties'
                      : (totalProperties ? ` of ${totalProperties.toLocaleString()}` : '') + ' properties'
                  }
                </p>
                {hasMore && !filters.favoritesOnly && (
                  <p className="text-sm text-primary mt-1">
                    More properties available - click "Load More" below
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Search saved automatically
              </p>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">View:</span>
              <Button
                onClick={() => setViewMode('grid')}
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                className="flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
                Cards
              </Button>
              
              <Button
                onClick={() => setViewMode('map')}
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                className="flex items-center"
              >
                <MapIcon />
                <span className="ml-1">Map</span>
              </Button>
            </div>
            </CardContent>
          </Card>
        )}

        {/* Properties Display */}
        {searchMode === 'filters' && getFilteredProperties().length > 0 && (
          <div>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getFilteredProperties().map((property) => (
                  <PropertyCard
                    key={property.roll_number}
                    property={property}
                    onPropertyClick={navigateToProperty}
                    toggleFavorite={handleToggleFavorite}
                    isFavorite={isFavorite}
                    getPropertyTags={getPropertyTags}
                    onOpenTagManager={handleOpenTagManager}
                    hasNote={hasNote}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapIcon />
                    <span className="ml-2">Property Locations</span>
                    <span className="ml-2 text-sm text-gray-500 font-normal">
                      ({getFilteredProperties().filter(p => p.centroid_lat && p.centroid_lon).length} properties with coordinates)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                <PropertyMap 
                  properties={getFilteredProperties()} 
                  isFavorite={isFavorite}
                  favoritesState={favorites}
                  onPropertyClick={navigateToProperty}
                />
                  <p className="text-xs text-gray-500 mt-3">
                    Click on map markers to see property details. Some properties may not appear if coordinates are unavailable.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Load More Button */}
        {searchMode === 'filters' && hasMore && !loading && !filters.favoritesOnly && (
          <div className="text-center my-8">
            <Button
              onClick={() => fetchProperties(false)}
              disabled={loadingMore}
              size="lg"
              className="flex items-center mx-auto"
            >
              {loadingMore ? (
                <>
                  <LoaderIcon />
                  <span className="ml-2">Loading More Properties...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M12 5v14"></path>
                    <path d="M5 12h14"></path>
                  </svg>
                  Load More Properties ({Math.min(250, (totalProperties || 0) - properties.length)} more available)
                </>
              )}
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              Loading 250 properties at a time for better performance
            </p>
          </div>
        )}

        {/* Empty State */}
        {searchMode === 'filters' && !loading && (
          (totalCount === 0 || ((filters.favoritesOnly || (filters.selectedTags && filters.selectedTags.length > 0)) && getFilteredProperties().length === 0)) && (
            <div className="text-center py-12">
              <div className="w-16 h-16 text-gray-300 mx-auto mb-4">
                {filters.favoritesOnly ? (
                  <HeartIcon className="w-16 h-16" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                )}
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {filters.favoritesOnly ? 'No Favorite Properties' : 'No Properties Found'}
              </h3>
              <p className="text-gray-500">
                {filters.favoritesOnly 
                  ? 'You haven\'t saved any favorite properties yet. Click the heart icon on any property to add it to your favorites.'
                  : 'Try adjusting your search filters to find more properties.'
                }
              </p>
            </div>
          )
        )}
      </div>
      
      {/* Tag Management Dialog */}
      {console.log('About to render PropertyTagManager:', { propertyToTag: !!propertyToTag })}
      <PropertyTagManager 
        property={propertyToTag}
        isOpen={!!propertyToTag}
        onClose={closeTagManager}
        getPropertyTags={getPropertyTags}
        getAllTags={getAllTags}
        addPropertyToTag={addPropertyToTag}
        removePropertyFromTag={removePropertyFromTag}
        createNewTag={createNewTag}
        updateTag={updateTag}
        deleteTag={deleteTag}
      />
      
      {/* Sign In Modal */}
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        signInWithGoogle={auth.signInWithGoogle}
        signInWithApple={auth.signInWithApple}
      />
      
      {/* Data Migration Prompt */}
      <DataMigrationPrompt
        isOpen={showMigrationPrompt}
        onMigrate={handleMigration}
        onSkip={handleSkipMigration}
        onClose={() => setShowMigrationPrompt(false)}
        favorites={favorites}
        tags={getAllTags()}
      />
    </div>
  );
};

// Global function for map popup favorite toggle
window.toggleMapFavorite = (propertyId) => {
  // This will be set by the PropertyExplorer component
  if (window.toggleFavoriteFunction) {
    window.toggleFavoriteFunction(propertyId);
  }
};

// Global function that also updates popups after toggling
window.toggleMapFavoriteAndUpdate = (propertyId) => {
  if (window.toggleFavoriteFunction) {
    window.toggleFavoriteFunction(propertyId);
    // The favorite state change will trigger the useEffect in PropertyMap automatically
  }
};

export default PropertyExplorer;